import axios from 'src/utils/axios';
import { trackEvent } from 'src/utils/posthog';
import { voolistExtension } from 'src/utils/voolist-extension';
import { getPlatformDisplayName } from 'src/utils/platform-label';
import { whatnotAspectsAsSent } from 'src/utils/whatnot-listing-details';
import {
  STALE_CLAIM_MS,
  createHeldBack,
  isUnconfirmedCreate,
  publishOutcomeEvent,
  unconfirmedCreateFlags,
} from 'src/utils/extension-publish-rules';

import { aspectsAsSent, routablePlacement } from 'src/sections/listing/utils/placement-values';

// ----------------------------------------------------------------------

/**
 * Creating ONE marketplace post through the Chrome extension, and recording what happened.
 *
 * Poshmark, Depop, Grailed, Whatnot and OfferUp have no server-to-server create API, so the post has to be made from
 * the seller's own browser. The server holds the work as a create-pending placement; this module is the
 * browser's half of that round trip — claim it, ask the extension to post it, tell the server how it
 * went. Every caller goes through here so the duplicate-post guards below cannot be forgotten by one
 * of them.
 */

// How long a create may go SILENT before we give up on it. The extension reports each step
// (photo 3 of 8, waiting for the photo check, creating the listing), and every report restarts
// this clock — so a create that is still working is never abandoned for being slow, only for
// stopping. Chrome throttles timers and network callbacks in hidden tabs, so a backgrounded run
// is given twice as long to speak up.
//
// The budget covers the stretches where nothing can be reported: waking a service worker Chrome
// has shut down, opening the marketplace tab and injecting the content script into it, and the
// marketplace's own answer to the create call. Giving up while one of those is still running is
// the most expensive mistake this module can make — the post can land afterwards, and from here
// that is indistinguishable from an extension that died, so the create has to be left unresolved
// for the seller to check. Headroom is therefore worth more than a fast verdict.
//
// NEITHER MAY EXCEED `STALE_CLAIM_MS`, and that is the ceiling on both. A create holds a claim that
// stops anything else posting the same item; once the claim is old enough to be taken over, a
// second attempt may start. So a budget longer than the claim's life leaves a window where the
// first create is still running and a second one is already allowed — two live posts. They were
// equal before, which hid the rule; the visible budget is raised into the headroom that leaves and
// the hidden one stays at the ceiling. Raising either further means raising the claim's life first,
// on the server as well, because the server's copy is the one that actually refuses a duplicate.
// Both are clamped to the ceiling rather than merely documented as being under it, so raising one
// can never quietly reopen that window.
const CREATE_SILENCE_LIMIT_MS = Math.min(90 * 1000, STALE_CLAIM_MS);
const CREATE_SILENCE_LIMIT_HIDDEN_MS = STALE_CLAIM_MS;

/**
 * What the extension posts to the marketplace, built from the listing as the server serves it.
 *
 * Deliberately the saved listing rather than the form's own state: the account's item-specifics and
 * its per-account title/price have already been resolved into `{platform}Aspects` by then, so every
 * caller publishes the same fields and none of them has to re-derive that mapping.
 *
 * This set must cover every listing field the extension reads on the other side — its adapters
 * (`chrome-extension/adapters/{depop,poshmark,grailed,whatnot,offerup,vestiaire}-adapter.js`) and its create/update handlers. A field
 * left out here does not fail: it arrives undefined and the marketplace post is silently written
 * without it, so adding a read over there means adding the field here.
 */
export function buildExtensionPayload(listing) {
  // The account's own title, price and description live in the placement's columns, not inside
  // {platform}Aspects — the listing endpoint keeps them out of that key on purpose, because it is
  // also read by the eBay item-specific builders and by "use as template". The form already puts the
  // two halves back together to display them (placementFormValues); aspectsAsSent does the same so
  // the marketplace is actually SENT what the seller was shown. Without it the override is collected,
  // displayed, stored, and then silently replaced by the listing's values at publish time.
  return {
    id: listing.id,
    title: listing.title,
    description: listing.plainDescription || listing.description,
    price: listing.price,
    currency: listing.currency,
    images: listing.images,
    brand: listing.brand,
    condition: listing.condition,
    primaryColor: listing.primaryColor,
    secondaryColor: listing.secondaryColor,
    quantity: listing.quantity,
    // The seller's own stock number, kept private by the marketplace — Poshmark stores it as
    // `seller_private_info.sku`, Depop takes its own from `depopAspects.sku`.
    sku: listing.sku,
    // What the item cost the seller, and its UPC, as Voolist holds them: Whatnot takes them as the listing's
    // cost per item and barcode. The extension decides what of them it sends (a cost above 0, a valid UPC).
    cost: listing.cost ?? null,
    upc: listing.upc ?? null,
    depopAspects: aspectsAsSent(listing, 'depop'),
    poshmarkAspects: aspectsAsSent(listing, 'poshmark'),
    grailedAspects: aspectsAsSent(listing, 'grailed'),
    // With the details the listing's own fields answer, and the category's answer to "Hazardous materials",
    // where the seller gave none: worked out now, so the listing as it stands is what Whatnot gets.
    whatnotAspects: whatnotAspectsAsSent(aspectsAsSent(listing, 'whatnot'), listing),
    // The seller's OfferUp answers (category, condition, size, pickup ZIP code) with the account's own title,
    // description and price, and what OfferUp said about the listing (its photos by uuid, the pickup place
    // last sent), which an edit sends back.
    offerupAspects: aspectsAsSent(listing, 'offerup'),
    // The seller's Vestiaire answers (sub-category, brand, material, size and the rest, in Vestiaire's own words)
    // with the account's own description and price, and what Vestiaire said about the listing (the photos last
    // sent, the draft a create was building), which an edit and a create cut short read back.
    vestiaireAspects: aspectsAsSent(listing, 'vestiaire'),
    // The package weight in grams, converted on the server: a Whatnot listing whose seller chose no shipping
    // profile is given the one whose weight band holds it.
    packageWeightGrams: listing.packageWeightGrams ?? null,
    // How the Depop account ships, which is a fact about the account rather than about this
    // listing — Depop offers postage labels to some accounts and not others, and an account
    // without them sends a flat price the seller sets. It sits beside the aspects rather than
    // inside them for that reason: nothing about the listing can change it.
    depopShipping: routablePlacement(listing?.platformAccounts, 'depop')?.depopShipping || null,
    voolistListingId: listing.id,
  };
}

/**
 * Ask the extension to create the post, and stop waiting once it goes quiet.
 * Never throws: a rejection becomes a failed outcome.
 *
 * One clock, owned by the bridge. A second timer here used to race the bridge's own and
 * always win, which produced a failure that was NOT marked as timed out — so a create that
 * was still running got reported to the server as failed, clearing the claim that stops the
 * post being created twice.
 */
async function askExtension(platform, payload, onProgress) {
  const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
  const timeoutMs = hidden ? CREATE_SILENCE_LIMIT_HIDDEN_MS : CREATE_SILENCE_LIMIT_MS;

  try {
    return await voolistExtension.crosslist(platform, payload, { timeoutMs, onProgress });
  } catch (err) {
    if (err?.isTimeout) {
      return {
        success: false,
        timedOut: true,
        error: `The Voolist extension didn't respond, so this item may already be on ${getPlatformDisplayName(platform)}. Check ${getPlatformDisplayName(platform)} before you try again, so it isn't listed twice.`,
      };
    }
    return { success: false, error: err?.message || 'Something went wrong in the Voolist extension. Please try again.' };
  }
}

/**
 * Build the recorder for one create attempt.
 *
 * Which listing and which screen are fixed for the whole attempt, so they are bound once here and
 * each terminal return only has to say how it ended. That is deliberate: a return that has to repeat
 * the screen is a return that can be written without it, and an outcome tagged with no screen is the
 * blind spot this property exists to remove.
 *
 * Every terminal return below goes through the recorder, so an attempt cannot finish without being
 * counted. That is what makes the publish success rate a rate: before this, only failures were
 * recorded, and a numerator with no denominator cannot say whether a seller's creates are working.
 *
 * `stage` is the one thing that varies per outcome, and only a failure needs it: a create can fall
 * over before the marketplace was ever asked, and a Voolist outage counted as marketplace rejections
 * is how we would end up blaming Poshmark and Depop for our own downtime. Everything past the claim
 * is 'publish', which is why that is the default. 'extension' is earlier still: the installed
 * extension is too old to work on this marketplace, so nothing was booked or asked.
 */
function outcomeRecorderFor(listingId, surface) {
  return (result, stage) => {
    const { event, properties } = publishOutcomeEvent(listingId, result, { stage, surface });
    trackEvent(event, properties);
    return result;
  };
}

/**
 * Claim one queued create, run it through the extension, and report the outcome to the server.
 *
 * Results carry the platform's RAW error text. Turning that into something a seller can read is the
 * rendering layer's job (src/utils/platform-error-message.js), so the same failure can be
 * phrased differently in different views.
 *
 * @param {string} listingId
 * @param {string} platform
 * @param {Object} payload - what the extension posts to the marketplace
 * @param {Object} [options]
 * @param {Function} [options.onProgress] - Called with `{ step, detail }` as the create proceeds,
 *   for a caller that wants to show what is happening. No UI uses it yet.
 * @param {string} [options.surface] - the screen this publish was started from, one of
 *   PUBLISH_SURFACES. Anything else is recorded as 'unknown' rather than taken at face value.
 * @returns {Promise<{success:boolean, platform:string, error?:string, alreadyCreated?:boolean,
 *   soldOut?:boolean, timedOut?:boolean, unrecorded?:boolean, marketplaceUnconfirmed?:boolean,
 *   extensionOutdated?:boolean, heldBack?:string, retryAfter?:number}>}
 *   `heldBack` means the marketplace's claim held the create back for now (createHeldBack): nothing was
 *   sent, the create stays queued, and `error` says when to try again. A claim refused because the listing still
 *   needs details the marketplace asks for (`notReady`) is a failure: nothing was sent, and `error` names them.
 *   `extensionOutdated` means the installed extension is too old for this marketplace: nothing was
 *   claimed or posted, nothing is recorded on the listing, and `error` tells the seller to update.
 *   `alreadyCreated` means this attempt posted nothing because there was nothing left to create;
 *   `soldOut` comes with it when that is because the item sold out and the sale withdrew the post
 *   that was waiting to go up. `timedOut`, `unrecorded` and `marketplaceUnconfirmed` each mean a post
 *   may be live that Voolist cannot account for — read them through isUnconfirmedCreate, never
 *   separately.
 */
export async function publishOneExtensionListing(listingId, platform, payload, { onProgress, surface } = {}) {
  const record = outcomeRecorderFor(listingId, surface);

  // An extension too old for this marketplace cannot post to it. Asked before the claim, so the
  // create stays waiting for a build that can, rather than being recorded as failed.
  const support = await voolistExtension.knowsMarketplace(platform);
  if (!support.known) {
    return record({ success: false, extensionOutdated: true, error: support.message, platform }, 'extension');
  }

  // Claim first: this is what stops a second tab (or a reload) from creating the same post twice,
  // and it is the only guard that survives this page going away.
  let claim;
  try {
    claim = (await axios.post(`/api/extension/platform-sync-claimed/${listingId}`, { platform })).data || {};
  } catch (claimErr) {
    return record({ success: false, error: "Couldn't start publishing. Refresh the page and try again.", platform }, 'claim');
  }

  // The marketplace's claim held this create back for now: its pace between new listings, the most it
  // is sent in a day, or Voolist having paused it (createHeldBack). Nothing was sent and the create
  // stays queued, so it is never reported failed and a later save or run sends it.
  const heldBack = createHeldBack(claim, platform);
  if (heldBack) return record(heldBack, 'claim');

  // The listing still needs details the marketplace asks for, as Voolist's rule finds them in it as it would be sent
  // (`notReady`, Vestiaire's), so the claim refused it: the create would stop for them anyway. It fails as the
  // browser's own check fails it — in the same words, which the claim gives — and is recorded on the listing the same way.
  if (claim.notReady === true) {
    await axios.post(`/api/extension/platform-sync-failed/${listingId}`, { platform, error: claim.error }).catch(() => {});
    return record({ success: false, error: claim.error, platform }, 'claim');
  }

  // There is nothing left to create: someone else is already creating this post, it is already up,
  // or the item sold out and the sale withdrew the post that was waiting to go up — that last one the
  // claim names, so the seller can be told. Either way there is nothing to do and nothing to report
  // as done. Recorded outside the try above: the catch there turns anything it sees into a claim
  // failure, which for a success would mean two contradictory events for one attempt.
  if (claim.alreadyClaimed) {
    return record(claim.soldOut
      ? { success: true, alreadyCreated: true, soldOut: true, platform }
      : { success: true, alreadyCreated: true, platform });
  }

  const outcome = await askExtension(platform, payload, onProgress);
  const rawError = outcome?.error || 'Unknown error';

  if (outcome?.success) {
    await axios.post(`/api/extension/platform-sync-complete/${listingId}`, { platform }).catch(() => {});
    return record({ success: true, platform });
  }

  // A create whose post may be live is the one failure we must not report to the server. Creating a
  // marketplace post cannot be undone or repeated safely: reporting it failed would clear the
  // server's claim, and with the post up and nothing holding the claim, the next save creates a
  // second one. Leaving the claim alone keeps that door shut — it expires by itself after the stale
  // window, and once the extension reports the marketplace id any later attempt is a no-op.
  //
  // Three endings qualify (see isUnconfirmedCreate): the extension went quiet, so nobody knows; the
  // marketplace failed or never answered, so it never said whether it made the post; or the
  // extension posted the item and could not save it here, so we know the post is live and only our
  // record of it is missing. None of them may be written down as a failure.
  //
  // A create the browser says sent nothing for review (`notSentForReview`, Vestiaire's) says so with its failure,
  // naming the claim this try made (`claimedAt`, as the claim answered it): Voolist's pace then frees the account's
  // next new listing sooner, and marks that claim only.
  if (!isUnconfirmedCreate(outcome)) {
    await axios.post(`/api/extension/platform-sync-failed/${listingId}`, {
      platform,
      error: rawError,
      ...(outcome?.notSentForReview === true ? { notSentForReview: true } : {}),
      ...(Number.isInteger(claim.claimedAt) ? { claimedAt: claim.claimedAt } : {}),
    }).catch(() => {});
  }

  return record({
    success: false,
    error: rawError,
    platform,
    ...unconfirmedCreateFlags(outcome),
  });
}
